<template>
  <div class="compra-registrar">

    <!-- --- CABECERA --- -->
    <div class="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-3">
      <div>
        <h4 class="compra-registrar__titulo mb-1">
          <i class="bi bi-cart-plus me-2"></i>Registrar Compra
        </h4>
        <p class="compra-registrar__subtitulo mb-0">
          Complete los datos manualmente o cargue el XML autorizado por el SRI para pre-llenar el formulario
        </p>
      </div>
      <div class="d-flex gap-2 align-items-center">
        <input ref="archivoXml" type="file" accept=".xml" hidden @change="onArchivoXmlSeleccionado" />
        <button class="btn btn-outline-primary" @click="$refs.archivoXml.click()">
          <i class="bi bi-file-earmark-arrow-up me-2"></i>Cargar XML del SRI
        </button>
      </div>
    </div>

    <!-- Banner XML cargado -->
    <div v-if="xmlCargado" class="alert alert-success alert-dismissible d-flex align-items-center gap-2 mb-4" role="alert">
      <i class="bi bi-check-circle-fill fs-5 flex-shrink-0"></i>
      <div>
        <strong>XML cargado correctamente.</strong>
        Los datos del comprobante han sido pre-llenados. Revise y corrija antes de guardar.
      </div>
      <button type="button" class="btn-close" @click="limpiarXml" aria-label="Limpiar XML"></button>
    </div>

    <!-- --- PASO 1: Formulario --- -->
    <div v-show="paso === 1">

    <!-- --- SECCIÓN 1: Datos del Comprobante --- -->
    <div class="compra-seccion mb-4">
      <div class="compra-seccion__cabecera">
        <i class="bi bi-file-earmark-check me-2"></i>Datos del Comprobante
      </div>
      <div class="compra-seccion__cuerpo">

        <!-- Número de Autorización — campo prominente y obligatorio -->
        <div class="mb-4">
          <label class="form-label fw-semibold">
            Número de Autorización SRI
            <span class="text-danger">*</span>
          </label>
          <div class="d-flex align-items-start gap-2 flex-wrap">
            <div class="flex-grow-1">
              <input
                type="text"
                v-model.trim="formulario.numeroAutorizacion"
                class="form-control font-monospace compra-input-autorizacion"
                :class="{ 'is-invalid': formulario.numeroAutorizacion && formulario.numeroAutorizacion.length !== 49 }"
                placeholder="Clave de acceso de 49 dígitos — Ej: 0104202601030262281600120010020000027505425731510"
                maxlength="49"
                inputmode="numeric"
              />
              <div v-if="!formulario.numeroAutorizacion" class="form-text">
                Número de 49 dígitos que valida que el comprobante fue autorizado por el SRI.
              </div>
              <div class="invalid-feedback" v-if="formulario.numeroAutorizacion && formulario.numeroAutorizacion.length !== 49">
                Debe tener exactamente 49 dígitos (actual: {{ formulario.numeroAutorizacion.length }})
              </div>
            </div>
            <span
              v-if="formulario.estadoSri"
              class="badge fs-6 px-3 py-2 compra-badge-estado"
              :class="estadoBadgeClase"
            >
              <i class="bi bi-shield-check me-1"></i>{{ formulario.estadoSri }}
            </span>
          </div>
        </div>

        <!-- Resto de campos del comprobante -->
        <div class="row g-3">
          <div class="col-sm-6 col-md-3">
            <label class="form-label">Tipo de Comprobante <span class="text-danger">*</span></label>
            <select class="form-select" v-model="formulario.tipoComprobante">
              <option value="01">Factura</option>
              <option value="03">Liquidación de Compra</option>
              <option value="05">Nota de Débito</option>
            </select>
          </div>
          <div class="col-sm-6 col-md-3">
            <label class="form-label">Número de Comprobante <span class="text-danger">*</span></label>
            <input
              type="text"
              class="form-control font-monospace"
              v-model="formulario.numeroComprobante"
              placeholder="001-001-000000001"
            />
          </div>
          <div class="col-sm-6 col-md-3">
            <label class="form-label">Fecha de Emisión <span class="text-danger">*</span></label>
            <input type="date" class="form-control" v-model="formulario.fechaEmision" />
          </div>
          <div class="col-sm-6 col-md-3" v-if="formulario.ambiente">
            <label class="form-label">Ambiente SRI</label>
            <input type="text" class="form-control" :value="formulario.ambiente" disabled />
          </div>
        </div>
      </div>
    </div>

    <!-- --- SECCIÓN 2: Proveedor --- -->
    <div class="compra-seccion mb-4">
      <div class="compra-seccion__cabecera">
        <i class="bi bi-building me-2"></i>Proveedor (Emisor del Comprobante)
      </div>
      <div class="compra-seccion__cuerpo">
        <div class="row g-3">
          <div class="col-sm-6 col-md-3">
            <label class="form-label">RUC / Identificación <span class="text-danger">*</span></label>
            <div class="input-group">
              <input
                type="text"
                class="form-control font-monospace"
                v-model.trim="formulario.proveedorRuc"
                placeholder="RUC o cédula"
                @blur="buscarProveedor"
                @keyup.enter="buscarProveedor"
              />
              <span v-if="buscandoProveedor" class="input-group-text">
                <span class="spinner-border spinner-border-sm"></span>
              </span>
            </div>
          </div>
          <div class="col-sm-6 col-md-5">
            <label class="form-label">Razón Social <span class="text-danger">*</span></label>
            <input
              type="text"
              class="form-control"
              v-model="formulario.proveedorNombre"
              placeholder="Nombre o razón social del proveedor"
            />
          </div>
          <div class="col-sm-6 col-md-4">
            <label class="form-label">Nombre Comercial</label>
            <input
              type="text"
              class="form-control"
              v-model="formulario.proveedorNombreComercial"
              placeholder="Nombre comercial (opcional)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- --- SECCIÓN 3: Detalles --- -->
    <div class="compra-seccion mb-4">
      <div class="compra-seccion__cabecera d-flex justify-content-between align-items-center">
        <span><i class="bi bi-list-ul me-2"></i>Detalle de Productos / Servicios</span>
        <button class="btn btn-sm btn-outline-primary" @click="agregarDetalle">
          <i class="bi bi-plus-lg me-1"></i>Agregar ítem
        </button>
      </div>
      <div class="compra-tabla-wrapper">
        <table class="compra-tabla">
          <thead>
            <tr>
              <th class="th-codigo">Código</th>
              <th class="th-descripcion">Descripción</th>
              <th class="th-num">Precio Unit.</th>
              <th class="th-num">Cantidad</th>
              <th class="th-num">Desc. ($)</th>
              <th class="th-sm">IVA %</th>
              <th class="th-num">Base Imponible</th>
              <th class="th-num">Valor IVA</th>
              <th class="th-num th-total">Total Línea</th>
              <th class="th-accion"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, idx) in formulario.detalles" :key="idx" class="fila-detalle">
              <td class="td-editable">
                <input
                  type="text"
                  v-model="item.codigo"
                  class="input-celda font-monospace"
                  maxlength="30"
                  placeholder="Código"
                />
              </td>
              <td class="td-editable">
                <input
                  type="text"
                  v-model="item.descripcion"
                  class="input-celda"
                  maxlength="300"
                  placeholder="Descripción del producto o servicio"
                />
              </td>
              <td class="td-editable">
                <input
                  type="number"
                  v-model.number="item.precioUnitario"
                  class="input-celda text-end"
                  @input="calcularLinea(item)"
                  min="0" step="0.0001"
                />
              </td>
              <td class="td-editable">
                <input
                  type="number"
                  v-model.number="item.cantidad"
                  class="input-celda text-end"
                  @input="calcularLinea(item)"
                  min="0.01" step="0.01"
                />
              </td>
              <td class="td-editable">
                <input
                  type="number"
                  v-model.number="item.descuento"
                  class="input-celda text-end"
                  @input="calcularLinea(item)"
                  min="0" step="0.01"
                />
              </td>
              <td class="td-editable">
                <select v-model.number="item.porcentajeIva" class="input-celda" @change="calcularLinea(item)">
                  <option :value="15">15 %</option>
                  <option :value="5">5 %</option>
                  <option :value="0">0 %</option>
                </select>
              </td>
              <td class="td-calculado text-end">$ {{ fmt(item.baseImponible) }}</td>
              <td class="td-calculado text-end">$ {{ fmt(item.montoIva) }}</td>
              <td class="td-calculado td-total text-end">$ {{ fmt(item.totalLinea) }}</td>
              <td class="td-accion text-center">
                <button
                  class="btn btn-sm btn-link text-danger p-1"
                  @click="formulario.detalles.splice(idx, 1)"
                  title="Eliminar fila"
                >
                  <i class="bi bi-trash3"></i>
                </button>
              </td>
            </tr>
            <tr v-if="formulario.detalles.length === 0">
              <td colspan="10" class="text-center text-muted fst-italic py-4">
                <i class="bi bi-inbox me-2"></i>Sin ítems. Agregue productos manualmente o cargue un XML del SRI.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- --- SECCIÓN 4: Pago + Totales --- -->
    <div class="row g-4 mb-4">

      <!-- Pago y observaciones -->
      <div class="col-lg-7">
        <div class="compra-seccion h-100 mb-0">
          <div class="compra-seccion__cabecera">
            <i class="bi bi-cash-stack me-2"></i>Pago y Observaciones
          </div>
          <div class="compra-seccion__cuerpo">
            <div class="row g-3">
              <div class="col-sm-6">
                <label class="form-label">Forma de Pago</label>
                <select class="form-select" v-model="formulario.formaPago">
                  <option value="01">Efectivo (sin sistema financiero)</option>
                  <option value="15">Compensación de deudas</option>
                  <option value="16">Tarjeta de débito</option>
                  <option value="19">Tarjeta de crédito</option>
                  <option value="20">Transferencia bancaria</option>
                  <option value="99">Otro</option>
                </select>
              </div>
              <div class="col-12">
                <label class="form-label">Observaciones</label>
                <textarea
                  class="form-control"
                  rows="3"
                  v-model="formulario.observaciones"
                  placeholder="Notas internas sobre esta compra (opcional)"
                ></textarea>
              </div>

              <!-- Compra a crédito -->
              <div class="col-12">
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" id="chkEsCredito" v-model="formulario.esCredito" />
                  <label class="form-check-label fw-semibold" for="chkEsCredito">
                    <i class="bi bi-calendar-check me-1"></i>Compra a crédito (pago diferido al proveedor)
                  </label>
                </div>
              </div>
              <template v-if="formulario.esCredito">
                <div class="col-sm-5 col-md-4">
                  <label class="form-label">Días de plazo <span class="text-danger">*</span></label>
                  <input
                    type="number"
                    class="form-control"
                    v-model.number="formulario.diasPlazo"
                    min="1"
                    max="365"
                    placeholder="30"
                  />
                  <div class="compra-plazos-rapidos mt-1">
                    <span class="text-muted" style="font-size:.78rem">Rápidos:</span>
                    <button type="button" class="compra-plazo-btn" @click="formulario.diasPlazo = 15">15d</button>
                    <button type="button" class="compra-plazo-btn" @click="formulario.diasPlazo = 30">30d</button>
                    <button type="button" class="compra-plazo-btn" @click="formulario.diasPlazo = 45">45d</button>
                    <button type="button" class="compra-plazo-btn" @click="formulario.diasPlazo = 60">60d</button>
                    <button type="button" class="compra-plazo-btn" @click="formulario.diasPlazo = 90">90d</button>
                  </div>
                </div>
                <div v-if="fechaVencimientoCompra" class="col-sm-7 col-md-8 d-flex align-items-end">
                  <div class="compra-vencimiento-info">
                    <i class="bi bi-calendar-event me-1"></i>
                    Vence el <strong>{{ fechaVencimientoCompra }}</strong>
                  </div>
                </div>
              </template>

              <!-- Registrar pago en caja -->
              <div class="col-12" v-if="!formulario.esCredito">
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" id="chkPagoCaja" v-model="cajaRegistrarPago"
                    @change="alActivarCaja" />
                  <label class="form-check-label fw-semibold" for="chkPagoCaja">
                    <i class="bi bi-cash-register me-1"></i>Registrar pago en caja ahora
                  </label>
                </div>
              </div>
              <template v-if="cajaRegistrarPago">
                <div class="col-12" v-if="sesionesAbiertas.length === 0">
                  <div class="alert alert-warning py-2 mb-0 small">
                    <i class="bi bi-exclamation-triangle me-1"></i>
                    No hay una sesión de caja abierta. Abra una caja en <strong>Finanzas › Caja</strong> para registrar el pago.
                  </div>
                </div>
                <template v-else>
                  <div class="col-sm-6">
                    <label class="form-label small fw-semibold">Caja activa</label>
                    <select class="form-select form-select-sm" v-model="sesionSeleccionadaId">
                      <option v-for="s in sesionesAbiertas" :key="s.id" :value="s.id">
                        {{ s.nombre_caja }}<template v-if="s.nombre_usuario"> — {{ s.nombre_usuario }}</template>
                      </option>
                    </select>
                  </div>
                  <div class="col-sm-6">
                    <label class="form-label small fw-semibold">Referencia del pago <span class="text-muted fw-normal">(opcional)</span></label>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      v-model="pagoReferencia"
                      placeholder="Ej: cheque, número de recibo…"
                      maxlength="100"
                    />
                  </div>
                </template>
              </template>

              <!-- Registrar pago en banco -->
              <div class="col-12" v-if="!formulario.esCredito">
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" id="chkPagoBanco" v-model="bancoRegistrarPago"
                    @change="alActivarBanco" />
                  <label class="form-check-label fw-semibold" for="chkPagoBanco">
                    <i class="bi bi-bank me-1"></i>Registrar pago en banco ahora
                  </label>
                </div>
              </div>
              <template v-if="bancoRegistrarPago">
                <div class="col-12" v-if="bancos.length === 0">
                  <div class="alert alert-warning py-2 mb-0 small">
                    <i class="bi bi-exclamation-triangle me-1"></i>
                    No hay cuentas bancarias registradas. Agréguelas en <strong>Finanzas › Bancos</strong>.
                  </div>
                </div>
                <template v-else>
                  <div class="col-sm-6">
                    <label class="form-label small fw-semibold">Cuenta bancaria</label>
                    <select class="form-select form-select-sm" v-model="bancoSeleccionadoId">
                      <option v-for="b in bancos" :key="b.id" :value="b.id">
                        {{ b.nombre_banco }} — {{ b.numero_cuenta }}
                      </option>
                    </select>
                  </div>
                  <div class="col-sm-6">
                    <label class="form-label small fw-semibold">Referencia del pago <span class="text-muted fw-normal">(opcional)</span></label>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      v-model="pagoReferencia"
                      placeholder="Ej: número de transferencia, comprobante…"
                      maxlength="100"
                    />
                  </div>
                </template>
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- Resumen de totales -->
      <div class="col-lg-5">
        <div class="compra-seccion compra-totales h-100 mb-0">
          <div class="compra-seccion__cabecera">
            <i class="bi bi-calculator me-2"></i>Resumen de Totales
          </div>
          <div class="compra-seccion__cuerpo">
            <!-- Subtotal sin impuestos -->
            <div class="compra-fila-total">
              <span>Subtotal:</span>
              <span class="fw-semibold">$ {{ fmt(totales.subtotalSinIva) }}</span>
            </div>
            <!-- Descuentos de línea (desde XML o manual) -->
            <div class="compra-fila-total" v-if="totales.totalDescuento > 0">
              <span class="text-muted">Descuentos:</span>
              <span class="text-muted">- $ {{ fmt(totales.totalDescuento) }}</span>
            </div>
            <!-- Descuentos adicionales -->
            <div class="compra-fila-total" v-if="totales.descuentoAdicional > 0">
              <span class="text-muted">Descuentos adicionales:</span>
              <span class="text-muted">- $ {{ fmt(totales.descuentoAdicional) }}</span>
            </div>
            <!-- IVA: una fila por cada tarifa presente (15, 5, 12, 0…) -->
            <template v-for="(montoIva, tarifa) in totales.ivaDetalle" :key="tarifa">
              <div class="compra-fila-total" v-if="Number(tarifa) > 0">
                <span>IVA {{ tarifa }} %:</span>
                <span>$ {{ fmt(montoIva) }}</span>
              </div>
            </template>
            <div class="compra-fila-total" v-if="Object.values(totales.ivaDetalle).every(v => v === 0)">
              <span>IVA:</span>
              <span>$ 0.00</span>
            </div>
            <hr class="my-2" />
            <div class="compra-fila-total compra-fila-total--grande">
              <span>TOTAL:</span>
              <span>$ {{ fmt(totales.total) }}</span>
            </div>
            <!-- Indicador: valores desde XML -->
            <div v-if="formulario.totalesXml" class="text-end mt-1" style="font-size:.72rem; color:var(--bs-success)">
              <i class="bi bi-file-earmark-check me-1"></i>Valores tomados del XML
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Alerta -->
    <div
      v-if="alerta.mensaje"
      :class="['alerta-sistema', `alerta-sistema--${alerta.tipo}`]"
      role="alert"
      :aria-live="alerta.tipo === 'error' ? 'assertive' : 'polite'"
    >
      <i :class="iconoAlerta(alerta.tipo)" class="alerta-sistema__icono"></i>
      <span class="alerta-sistema__texto">{{ alerta.mensaje }}</span>
      <button type="button" class="alerta-sistema__cerrar" @click="alerta.mensaje = ''" aria-label="Cerrar">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
    </div>

    <!-- Acciones paso 1 -->
    <div class="d-flex gap-3 justify-content-end pb-4">
      <router-link to="/compras/ordenes" class="btn btn-outline-secondary px-4">
        <i class="bi bi-x-lg me-1"></i>Cancelar
      </router-link>
      <button class="btn btn-primary px-4" @click="revisarProductos" :disabled="procesando || !!compraDuplicadaId">
        <span v-if="procesando" class="spinner-border spinner-border-sm me-2"></span>
        <i v-else class="bi bi-arrow-right-circle me-2"></i>
        Revisar Productos →
      </button>
    </div>

    </div><!-- /paso 1 -->

    <!-- --- PASO 2: HOMOLOGACIÓN DE PRODUCTOS --- -->
    <div v-show="paso === 2">

      <!-- Cabecera paso 2 -->
      <div class="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
        <div class="d-flex align-items-center gap-3">
          <span class="fw-bold" style="color:var(--primary-color); font-size:.95rem">
            <i class="bi bi-boxes me-1"></i>Homologación de Productos
          </span>
          <span class="text-muted small">{{ homologacionProgreso }}/{{ homologacion.length }} revisados</span>
          <span v-if="homologacion.length > 0 && homologacionProgreso === homologacion.length" class="badge bg-success">
            <i class="bi bi-check-circle-fill me-1"></i>Completo
          </span>
        </div>
        <button class="btn btn-sm btn-outline-secondary" @click="volverFormulario" :disabled="procesando">
          <i class="bi bi-arrow-left me-1"></i>Volver
        </button>
      </div>

      <!-- Barra de progreso -->
      <div class="mb-3">
        <div class="progress" style="height:4px;">
          <div
            class="progress-bar bg-success"
            :style="`width:${homologacion.length ? (homologacionProgreso / homologacion.length * 100) : 0}%`"
          ></div>
        </div>
      </div>

      <!-- Ítems de homologación -->
      <div v-for="(hItem, idx) in homologacion" :key="idx" class="homolog-item mb-2">

        <!-- Cabecera del ítem -->
        <div class="homolog-item__header">
          <div class="d-flex align-items-center gap-1 flex-wrap">
            <span class="badge bg-secondary" style="font-size:.7rem">{{ idx + 1 }}</span>
            <span v-if="hItem.codigo" class="font-monospace text-muted" style="font-size:.72rem">{{ hItem.codigo }}</span>
            <span class="fw-semibold flex-grow-1 text-truncate" style="max-width:45%;font-size:.85rem">{{ hItem.descripcion }}</span>
            <span class="ms-auto text-muted" style="font-size:.75rem">
              {{ hItem.cantidad }}u · ${{ fmt(hItem.precioUnitario) }} · IVA {{ hItem.porcentajeIva }}%
            </span>
            <span v-if="hItem.accion === 'vincular' && hItem.productoVinculado" class="badge bg-success" style="font-size:.7rem"><i class="bi bi-link-45deg"></i> Vinc.</span>
            <span v-else-if="hItem.accion === 'crear'" class="badge bg-primary" style="font-size:.7rem"><i class="bi bi-plus"></i> Nuevo</span>
            <span v-else-if="hItem.accion === 'omitir'" class="badge bg-warning text-dark" style="font-size:.7rem"><i class="bi bi-eye-slash"></i> Omit.</span>
            <span v-if="hItem.precioUnitario === 0" class="badge bg-danger" style="font-size:.7rem">$0</span>
          </div>
        </div>

        <!-- Cuerpo del ítem -->
        <div class="homolog-item__body">

          <!-- Radio opciones -->
          <div class="d-flex gap-1 mb-2 flex-wrap">
            <label class="homolog-opcion homolog-opcion--sm" :class="{ 'homolog-opcion--activa': hItem.accion === 'vincular' }">
              <input type="radio" :name="`accion-${idx}`" value="vincular" v-model="hItem.accion" class="me-1" />
              <i class="bi bi-link-45deg me-1" style="color:var(--accent-success)"></i>Vincular
            </label>
            <label class="homolog-opcion homolog-opcion--sm" :class="{ 'homolog-opcion--activa': hItem.accion === 'crear' }">
              <input type="radio" :name="`accion-${idx}`" value="crear" v-model="hItem.accion" class="me-1" />
              <i class="bi bi-plus-circle me-1" style="color:var(--primary-color)"></i>Crear
            </label>
            <label class="homolog-opcion homolog-opcion--sm" :class="{ 'homolog-opcion--activa': hItem.accion === 'omitir' }">
              <input type="radio" :name="`accion-${idx}`" value="omitir" v-model="hItem.accion" class="me-1" />
              <i class="bi bi-eye-slash me-1" style="color:var(--accent-warning)"></i>Omitir
            </label>
          </div>

          <!-- ── Panel VINCULAR ── -->
          <div v-if="hItem.accion === 'vincular'" class="homolog-panel">
            <div v-if="hItem.cargandoCandidatos" class="text-muted small py-2">
              <span class="spinner-border spinner-border-sm me-1"></span>Buscando…
            </div>
            <div v-else>
              <!-- Candidatos automáticos -->
              <div v-if="hItem.candidatos.length > 0" class="homolog-candidatos mb-2">
                <div
                  v-for="cand in hItem.candidatos" :key="cand.id"
                  class="homolog-candidato homolog-candidato--compact"
                  :class="{ 'homolog-candidato--seleccionado': hItem.productoVinculado?.id === cand.id }"
                  @click="seleccionarProducto(hItem, cand)"
                >
                  <span class="font-monospace text-muted" style="font-size:.72rem">{{ cand.codigo_principal }}</span>
                  <span class="fw-semibold mx-2" style="font-size:.82rem">{{ cand.nombre_producto }}</span>
                  <span v-if="cand.nombre_categoria" class="badge bg-light text-secondary border" style="font-size:.65rem">{{ cand.nombre_categoria }}</span>
                  <span class="ms-auto fw-semibold text-end" style="font-size:.8rem">${{ fmt(parseFloat(cand.costo)) }}</span>
                </div>
              </div>
              <div v-else class="text-warning-emphasis small mb-2">
                <i class="bi bi-exclamation-triangle me-1"></i>Sin coincidencias automáticas.
              </div>

              <!-- Búsqueda manual (tiempo real) -->
              <div class="d-flex gap-1 mb-2">
                <input
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="Buscar por SKU, código o nombre…"
                  v-model="hItem.busquedaManual"
                  @input="debounceBuscarManual(hItem)"
                  @keyup.enter="buscarProductoManual(hItem)"
                />
                <button class="btn btn-outline-secondary btn-sm" @click="buscarProductoManual(hItem)" :disabled="hItem.buscandoManual">
                  <span v-if="hItem.buscandoManual" class="spinner-border spinner-border-sm"></span>
                  <i v-else class="bi bi-search"></i>
                </button>
              </div>

              <!-- Resultados búsqueda manual -->
              <div v-if="hItem.resultadosBusqueda.length > 0" class="homolog-candidatos mb-2">
                <div
                  v-for="res in hItem.resultadosBusqueda" :key="res.id"
                  class="homolog-candidato homolog-candidato--compact"
                  :class="{ 'homolog-candidato--seleccionado': hItem.productoVinculado?.id === res.id }"
                  @click="seleccionarProducto(hItem, res)"
                >
                  <span class="font-monospace text-muted" style="font-size:.72rem">{{ res.codigo_principal }}</span>
                  <span class="fw-semibold mx-2" style="font-size:.82rem">{{ res.nombre_producto }}</span>
                  <span class="ms-auto fw-semibold" style="font-size:.8rem">${{ fmt(parseFloat(res.costo)) }}</span>
                </div>
              </div>

              <!-- Producto seleccionado -->
              <div v-if="hItem.productoVinculado" class="homolog-seleccionado">
                <div class="d-flex align-items-center gap-2">
                  <i class="bi bi-check-circle-fill text-success"></i>
                  <span class="fw-semibold" style="font-size:.85rem">{{ hItem.productoVinculado.nombre_producto }}</span>
                  <span class="font-monospace text-muted" style="font-size:.72rem">{{ hItem.productoVinculado.codigo_principal }}</span>
                  <button class="btn btn-link btn-sm text-danger p-0 ms-auto" style="font-size:.75rem" @click="hItem.productoVinculado = null; hItem.actualizarCosto = false; hItem.opcionCostoCero = 'sin_stock'; hItem.preciosVinculado = []; hItem.convertirUnidad = false; hItem.factorConversion = null; hItem.unidadConversionId = null">
                    <i class="bi bi-x-circle me-1"></i>Cambiar
                  </button>
                </div>

                <!-- Costo $0 -->
                <div v-if="hItem.precioUnitario === 0" class="mt-2 p-2 rounded border d-flex align-items-center gap-3 flex-wrap" style="background:rgba(220,53,69,0.05);border-color:rgba(220,53,69,0.25)!important;font-size:.82rem">
                  <span class="text-danger fw-semibold"><i class="bi bi-exclamation-triangle-fill me-1"></i>Costo $0 — stock:</span>
                  <label class="homolog-opcion homolog-opcion--sm" :class="{ 'homolog-opcion--activa': hItem.opcionCostoCero === 'sin_stock' }">
                    <input type="radio" :name="`cero-${idx}`" value="sin_stock" v-model="hItem.opcionCostoCero" class="me-1" />
                    <i class="bi bi-eye-slash me-1 text-warning"></i>Sin stock
                  </label>
                  <label class="homolog-opcion homolog-opcion--sm" :class="{ 'homolog-opcion--activa': hItem.opcionCostoCero === 'ultimo_costo' }">
                    <input type="radio" :name="`cero-${idx}`" value="ultimo_costo" v-model="hItem.opcionCostoCero" class="me-1" />
                    <i class="bi bi-clock-history me-1 text-success"></i>Usar costo vigente (<strong>${{ fmt(parseFloat(hItem.productoVinculado.costo)) }}</strong>)
                  </label>
                </div>

                <!-- Actualizar costo (costo > 0 y diferente) -->
                <div
                  v-else-if="parseFloat(hItem.productoVinculado.costo) !== hItem.precioUnitario"
                  class="mt-2 p-2 rounded border d-flex align-items-center gap-2 flex-wrap"
                  style="background:rgba(255,193,7,0.07);font-size:.82rem"
                >
                  <div class="form-check mb-0">
                    <input type="checkbox" class="form-check-input" :id="`act-costo-${idx}`" v-model="hItem.actualizarCosto"
                      @change="hItem.preciosVinculado.forEach(p => calcularPrecioVinculado(hItem, p))" />
                    <label class="form-check-label" :for="`act-costo-${idx}`">
                      Actualizar costo:
                      <del class="text-danger ms-1">${{ fmt(parseFloat(hItem.productoVinculado.costo)) }}</del>
                      → <span class="text-success fw-semibold">${{ fmt(hItem.precioUnitario) }}</span>
                      <span v-if="hItem.productoVinculado.costo_anterior > 0" class="text-muted ms-2" style="font-size:.75rem">(ant. ${{ fmt(parseFloat(hItem.productoVinculado.costo_anterior)) }})</span>
                    </label>
                  </div>
                </div>
                <div v-else class="mt-1 small text-success" style="font-size:.78rem">
                  <i class="bi bi-check2 me-1"></i>Costo coincide.
                </div>

                <!-- ── Precios de venta del producto vinculado ── -->
                <div class="mt-3">
                  <p class="small fw-semibold mb-1">
                    <i class="bi bi-tags me-1"></i>Precios de venta
                    <span class="fw-normal text-muted">
                      <template v-if="hItem.convertirUnidad && hItem.factorConversion > 1">
                        (base: ${{ fmt(hItem.precioUnitario / hItem.factorConversion) }}/{{ unidadesMedida.find(u => u.id === hItem.unidadConversionId)?.abreviatura || 'u' }}
                        — costo por unidad mínima)
                      </template>
                      <template v-else>
                        (base: ${{ fmt(hItem.precioUnitario) }}{{ hItem.actualizarCosto ? ' — nuevo costo' : hItem.precioUnitario !== parseFloat(hItem.productoVinculado.costo) ? ' — costo factura' : '' }})
                      </template>
                    </span>
                  </p>
                  <div v-if="hItem.cargandoPreciosVinculado" class="text-muted small py-1">
                    <span class="spinner-border spinner-border-sm me-1"></span>Cargando precios…
                  </div>
                  <div v-else class="table-responsive">
                    <table class="table table-sm table-bordered" style="max-width:640px">
                      <thead>
                        <tr style="background:var(--secondary-bg-soft,#f1f5f9)">
                          <th class="small">Lista</th>
                          <th class="text-end small" style="width:130px">% Margen</th>
                          <th class="text-end small" style="width:110px">Precio</th>
                          <th class="text-end small" style="width:120px">+IVA ({{ hItem.porcentajeIva }}%)</th>
                          <th class="text-center small" style="width:60px">Activo</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(p, pi) in hItem.preciosVinculado" :key="pi">
                          <td class="small">{{ p.tipo_precio }}</td>
                          <td>
                            <div class="input-group input-group-sm">
                              <input
                                type="number"
                                class="form-control form-control-sm text-end"
                                v-model.number="p.porcentaje"
                                @input="calcularPrecioVinculado(hItem, p)"
                                min="0"
                                step="0.1"
                              />
                              <span class="input-group-text small">%</span>
                            </div>
                          </td>
                          <td class="text-end fw-semibold small">${{ fmt(p.precio) }}</td>
                          <td class="text-end small text-success fw-semibold">${{ fmt(p.precio * (1 + hItem.porcentajeIva / 100)) }}</td>
                          <td class="text-center"><input type="checkbox" v-model="p.activo" /></td>
                        </tr>
                      </tbody>
                    </table>
                    <button
                      v-if="hItem.preciosVinculado.length < 5"
                      type="button"
                      class="btn btn-outline-secondary btn-sm mt-1"
                      style="font-size:.75rem"
                      @click="agregarPrecioVinculado(hItem)"
                    >
                      <i class="bi bi-plus-circle me-1"></i>Agregar precio
                    </button>
                  </div>

                <!-- ── Conversión de unidades ── -->
                <div class="mt-3 p-2 rounded border" style="background:rgba(17,115,212,0.04);border-color:rgba(17,115,212,0.2)!important">
                  <div class="form-check mb-0">
                    <input type="checkbox" class="form-check-input" :id="`conv-${idx}`" v-model="hItem.convertirUnidad" />
                    <label class="form-check-label small fw-semibold" :for="`conv-${idx}`">
                      <i class="bi bi-arrow-left-right me-1 text-primary"></i>Convertir unidad de medida al registrar en inventario
                    </label>
                  </div>
                  <div v-if="hItem.convertirUnidad" class="mt-2" style="font-size:.82rem">
                    <div class="d-flex align-items-center gap-2 flex-wrap">
                      <span class="text-muted">1 {{ hItem.productoVinculado?.nombre_unidad || 'unidad' }} (factura)
                        <i class="bi bi-arrow-right mx-1"></i>
                      </span>
                      <div style="width:100px">
                        <input
                          type="number"
                          class="form-control form-control-sm text-end"
                          v-model.number="hItem.factorConversion"
                          min="0.0001"
                          step="1"
                          placeholder="305"
                        />
                      </div>
                      <div style="min-width:150px">
                        <select class="form-select form-select-sm" v-model="hItem.unidadConversionId">
                          <option :value="null" disabled>— unidad destino —</option>
                          <option
                            v-for="um in unidadesMedida"
                            :key="um.id"
                            :value="um.id"
                          >{{ um.unidad_medida }} ({{ um.abreviatura }})</option>
                        </select>
                      </div>
                      <span class="text-muted small">en inventario</span>
                    </div>
                    <div
                      v-if="hItem.factorConversion > 0 && hItem.unidadConversionId"
                      class="mt-1 d-flex gap-3 flex-wrap"
                      style="font-size:.8rem"
                    >
                      <span class="text-success fw-semibold">
                        <i class="bi bi-boxes me-1"></i>Stock:
                        {{ fmt(hItem.cantidad * (hItem.factorConversion || 1)) }}
                        {{ unidadesMedida.find(u => u.id === hItem.unidadConversionId)?.abreviatura || '' }}
                      </span>
                      <span class="text-info fw-semibold">
                        <i class="bi bi-tag me-1"></i>Costo/u:
                        ${{ fmt(hItem.precioUnitario / (hItem.factorConversion || 1)) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div><!-- /panel vincular -->

          <!-- ── Panel CREAR ── -->
          <div v-if="hItem.accion === 'crear'" class="homolog-panel">
            <div class="row gy-2">
              <div class="col-md-3">
                <label class="form-label small fw-semibold mb-1">Código principal <span class="text-danger">*</span></label>
                <input
                  type="text"
                  class="form-control form-control-sm font-monospace"
                  v-model="hItem.nuevoCodigo"
                  maxlength="50"
                  placeholder="Ej: SKU, código interno"
                />
              </div>
              <div class="col-md-3">
                <label class="form-label small fw-semibold mb-1">Código secundario</label>
                <input
                  type="text"
                  class="form-control form-control-sm font-monospace"
                  v-model="hItem.nuevoCodigoAuxiliar"
                  maxlength="50"
                  placeholder="Ej: código de barra, EAN"
                />
              </div>
              <div class="col-md-3">
                <label class="form-label small fw-semibold mb-1">Categoría <span class="text-danger">*</span></label>
                <select class="form-select form-select-sm" v-model="hItem.categoriaId">
                  <option value="">— Seleccionar categoría —</option>
                  <option v-for="cat in categorias" :key="cat.id" :value="cat.id">{{ cat.categoria }}</option>
                </select>
              </div>
              <div class="col-md-2">
                <label class="form-label small fw-semibold mb-1">Tipo</label>
                <select class="form-select form-select-sm" v-model="hItem.tipoProd">
                  <option value="bien">Bien</option>
                  <option value="servicio">Servicio</option>
                </select>
              </div>
              <div class="col-md-1" v-if="hItem.tipoProd === 'bien'">
                <label class="form-label small fw-semibold mb-1">Unidad</label>
                <select class="form-select form-select-sm" v-model="hItem.unidadMedidaId">
                  <option value="">—</option>
                  <option v-for="u in unidadesMedida" :key="u.id" :value="u.id">{{ u.unidad_medida }}</option>
                </select>
              </div>
            </div>

            <!-- Precios de venta -->
            <div class="mt-2">
              <p class="small fw-semibold mb-1">
                <i class="bi bi-tags me-1"></i>Precios de venta <span class="fw-normal text-muted">(costo base: ${{ fmt(hItem.precioUnitario) }})</span>
              </p>
              <div class="table-responsive">
                <table class="table table-sm table-bordered" style="max-width:620px">
                  <thead>
                    <tr style="background:var(--secondary-bg-soft,#f1f5f9)">
                      <th class="small">Lista</th>
                      <th class="text-end small" style="width:120px">% Margen</th>
                      <th class="text-end small" style="width:110px">Precio</th>
                      <th class="text-end small" style="width:110px">+IVA ({{ hItem.porcentajeIva }}%)</th>
                      <th class="text-center small" style="width:60px">Activo</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(p, pi) in hItem.precios" :key="pi">
                      <td class="small">{{ p.tipo_precio }}</td>
                      <td>
                        <div class="input-group input-group-sm">
                          <input
                            type="number"
                            class="form-control form-control-sm text-end"
                            v-model.number="p.porcentaje"
                            @input="calcularPrecio(hItem, p)"
                            min="0"
                            step="0.1"
                          />
                          <span class="input-group-text small">%</span>
                        </div>
                      </td>
                      <td class="text-end fw-semibold small">${{ fmt(p.precio) }}</td>
                      <td class="text-end small text-success fw-semibold">${{ fmt(p.precio * (1 + hItem.porcentajeIva / 100)) }}</td>
                      <td class="text-center"><input type="checkbox" v-model="p.activo" /></td>
                    </tr>
                  </tbody>
                </table>
                <button
                  v-if="hItem.precios.length < 5"
                  type="button"
                  class="btn btn-outline-secondary btn-sm mt-1"
                  style="font-size:.75rem"
                  @click="agregarPrecioCrear(hItem)"
                >
                  <i class="bi bi-plus-circle me-1"></i>Agregar precio
                </button>
              </div>
            </div>

            <!-- Conversion de unidades para productos nuevos -->
            <div class="mt-3 p-2 rounded border" style="background:rgba(17,115,212,0.04);border-color:rgba(17,115,212,0.2)!important">
              <div class="form-check mb-0">
                <input
                  type="checkbox"
                  class="form-check-input"
                  :id="`conv-crear-${idx}`"
                  v-model="hItem.convertirUnidad"
                  @change="hItem.precios.forEach(p => calcularPrecio(hItem, p))"
                />
                <label class="form-check-label small fw-semibold" :for="`conv-crear-${idx}`">
                  <i class="bi bi-arrow-left-right me-1 text-primary"></i>Convertir unidad de medida al crear el producto
                </label>
              </div>
              <div v-if="hItem.convertirUnidad" class="mt-2" style="font-size:.82rem">
                <div class="d-flex align-items-center gap-2 flex-wrap">
                  <span class="text-muted">1 unidad de factura <i class="bi bi-arrow-right mx-1"></i></span>
                  <div style="width:100px">
                    <input
                      type="number"
                      class="form-control form-control-sm text-end"
                      v-model.number="hItem.factorConversion"
                      min="0.0001"
                      step="1"
                      placeholder="305"
                      @input="hItem.precios.forEach(p => calcularPrecio(hItem, p))"
                    />
                  </div>
                  <div style="min-width:150px">
                    <select class="form-select form-select-sm" v-model="hItem.unidadConversionId">
                      <option :value="null" disabled>-- unidad destino --</option>
                      <option
                        v-for="um in unidadesMedida"
                        :key="um.id"
                        :value="um.id"
                      >{{ um.unidad_medida }} ({{ um.abreviatura }})</option>
                    </select>
                  </div>
                  <span class="text-muted small">en inventario</span>
                </div>
                <div v-if="hItem.factorConversion > 0 && hItem.unidadConversionId" class="mt-1 d-flex gap-3 flex-wrap" style="font-size:.8rem">
                  <span class="text-success fw-semibold">
                    <i class="bi bi-boxes me-1"></i>Stock:
                    {{ fmt(hItem.cantidad * (hItem.factorConversion || 1)) }}
                    {{ unidadesMedida.find(u => u.id === hItem.unidadConversionId)?.abreviatura || '' }}
                  </span>
                  <span class="text-info fw-semibold">
                    <i class="bi bi-tag me-1"></i>Costo/u:
                    ${{ fmt(hItem.precioUnitario / (hItem.factorConversion || 1)) }}
                  </span>
                </div>
              </div>
            </div>
          </div><!-- /panel crear -->

          <!-- ── Panel OMITIR ── -->
          <div v-if="hItem.accion === 'omitir'" class="homolog-panel">
            <p class="small text-warning-emphasis mb-0"><i class="bi bi-eye-slash me-1"></i>Sin movimiento de inventario para este ítem.</p>
          </div><!-- /panel omitir -->

        </div><!-- /homolog-item__body -->
      </div><!-- /homolog-item -->

      <!-- Alerta paso 2 -->
      <div
        v-if="alerta.mensaje"
        :class="['alerta-sistema', `alerta-sistema--${alerta.tipo}`]"
        role="alert"
        :aria-live="alerta.tipo === 'error' ? 'assertive' : 'polite'"
      >
        <i :class="iconoAlerta(alerta.tipo)" class="alerta-sistema__icono"></i>
        <span class="alerta-sistema__texto">{{ alerta.mensaje }}</span>
        <button type="button" class="alerta-sistema__cerrar" @click="alerta.mensaje = ''" aria-label="Cerrar">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      <!-- Panel de factura duplicada -->
      <div v-if="compraDuplicadaId" class="alerta-sistema alerta-sistema--advertencia alerta-sistema--con-acciones" role="alert">
        <i class="bi bi-exclamation-triangle-fill alerta-sistema__icono"></i>
        <div class="alerta-sistema__cuerpo">
          <span class="alerta-sistema__texto">Esta factura ya fue registrada anteriormente.</span>
          <div class="alerta-sistema__botones">
            <button class="alerta-sistema__btn alerta-sistema__btn--principal" @click="$router.push('/compras/ordenes/' + compraDuplicadaId)">
              <i class="bi bi-eye me-1"></i>Ver compra registrada
            </button>
            <button class="alerta-sistema__btn alerta-sistema__btn--secundario" @click="$router.push('/compras/ordenes')">
              <i class="bi bi-list me-1"></i>Ir al listado
            </button>
          </div>
        </div>
        <button type="button" class="alerta-sistema__cerrar" @click="compraDuplicadaId = null" aria-label="Cerrar">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      <!-- Acciones paso 2 -->
      <div class="d-flex gap-3 justify-content-end pb-4">
        <button class="btn btn-outline-secondary px-4" @click="volverFormulario" :disabled="procesando">
          <i class="bi bi-arrow-left me-1"></i>Volver al formulario
        </button>
        <button class="btn btn-success px-4" @click="guardar" :disabled="procesando">
          <span v-if="procesando" class="spinner-border spinner-border-sm me-2"></span>
          <i v-else class="bi bi-floppy2 me-2"></i>
          Confirmar y Guardar Compra
        </button>
      </div>

    </div><!-- /paso 2 -->

  </div>
</template>

<script>
import api from '@/services/api'

function hoy() {
  const ahora = new Date()
  return new Date(ahora.getTime() - 5 * 60 * 60 * 1000).toISOString().slice(0, 10)
}

/** Decodifica entidades HTML residuales que pueden quedar al parsear XML con CDATA */
function decodificarTexto(str) {
  if (!str) return ''
  const el = document.createElement('textarea')
  el.innerHTML = str
  return el.value
}

function nuevoDetalle() {
  return {
    codigo: '',
    descripcion: '',
    precioUnitario: 0,
    cantidad: 1,
    descuento: 0,
    porcentajeIva: 15,
    baseImponible: 0,
    montoIva: 0,
    totalLinea: 0,
  }
}

export default {
  name: 'ClientComprasRegistrar',

  data() {
    return {
      paso: 1,
      buscandoProveedor: false,
      xmlCargado: false,

      formulario: {
        // Comprobante
        numeroAutorizacion: '',
        estadoSri: '',
        ambiente: '',
        tipoComprobante: '01',
        numeroComprobante: '',
        fechaEmision: hoy(),
        identificacionComprador: '',
        // Proveedor
        proveedorRuc: '',
        proveedorNombre: '',
        proveedorNombreComercial: '',
        proveedorId: null,
        // Pago
        formaPago: '01',
        observaciones: '',
        // Crédito
        esCredito: false,
        diasPlazo: 30,
        // Detalles
        detalles: [],
        // Totales importados directamente desde el XML (null = entrada manual)
        totalesXml: null,
      },

      // Paso 2 — homologación
      homologacion: [],
      categorias: [],
      unidadesMedida: [],

      // Caja — pago al registrar
      cajaRegistrarPago: false,
      sesionActiva: null,
      sesionesAbiertas: [],
      sesionSeleccionadaId: null,
      pagoReferencia: '',
      // Banco — pago al registrar
      bancoRegistrarPago: false,
      bancos: [],
      bancoSeleccionadoId: null,

      procesando: false,
      alerta: { mensaje: '', tipo: 'error' },
      compraDuplicadaId: null,
    }
  },

  computed: {
    totales() {
      const xml               = this.formulario.totalesXml
      const descuentoAdicional = xml
        ? xml.descuentoAdicional
        : this.obtenerDescuentoAdicionalXml()

      // Descuentos de línea: directamente desde el XML (NO totalDescuento, ese ya incluye el adicional)
      const totalDescuento = xml
        ? xml.descuentoLineas
        : this.formulario.detalles.reduce((acc, d) => acc + (d.descuento || 0), 0)

      // Subtotal sin impuestos: desde XML o suma de baseImponible de líneas
      const subtotalSinIva = xml
        ? xml.totalSinImpuestos
        : this.formulario.detalles.reduce((acc, d) => acc + (d.baseImponible || 0), 0)

      // IVA: acumular todas las tarifas presentes (puede haber 15, 5, 12, 0, etc.)
      const ivaDetalle = {} // { tarifa: monto }
      if (xml) {
        Object.assign(ivaDetalle, xml.impuestos)
      } else {
        for (const d of this.formulario.detalles) {
          const t = d.porcentajeIva || 0
          ivaDetalle[t] = (ivaDetalle[t] || 0) + (d.montoIva || 0)
        }
      }

      // Atajos para compatibilidad con el template
      const ivaQuince = ivaDetalle[15] || ivaDetalle[14] || ivaDetalle[12] || 0
      const ivaCinco  = ivaDetalle[5] || 0

      // Total: desde XML o calculado
      let total
      if (xml) {
        total = xml.importeTotal
      } else {
        const ivaTotal = Object.values(ivaDetalle).reduce((a, v) => a + v, 0)
        total = Math.max(0, subtotalSinIva + ivaTotal - descuentoAdicional)
      }

      return { subtotalSinIva, totalDescuento, descuentoAdicional, ivaDetalle, ivaQuince, ivaCinco, total }
    },

    estadoBadgeClase() {
      const estado = (this.formulario.estadoSri || '').toUpperCase()
      if (estado === 'AUTORIZADO') return 'bg-success'
      if (estado === 'RECHAZADO') return 'bg-danger'
      return 'bg-secondary'
    },

    homologacionProgreso() {
      return this.homologacion.filter(h =>
        h.accion === 'omitir' ||
        h.accion === 'crear' ||
        (h.accion === 'vincular' && h.productoVinculado !== null)
      ).length
    },

    fechaVencimientoCompra() {
      if (!this.formulario.esCredito || !this.formulario.fechaEmision || !this.formulario.diasPlazo) return null
      const base = new Date(this.formulario.fechaEmision)
      base.setDate(base.getDate() + Number(this.formulario.diasPlazo))
      return base.toLocaleDateString('es-EC', { year: 'numeric', month: 'long', day: 'numeric' })
    },
  },

  watch: {
    // Cuando se selecciona transferencia bancaria, sugerir pago en banco
    'formulario.formaPago'(val) {
      if (val === '20') {
        // Transferencia bancaria → activar banco, desactivar caja
        this.bancoRegistrarPago = true
        this.cajaRegistrarPago  = false
        if (this.bancos.length > 0 && !this.bancoSeleccionadoId) {
          this.bancoSeleccionadoId = this.bancos[0].id
        }
      }
    },
  },

  mounted() {
    this.agregarDetalle()
    this.cargarCatalogos()
  },

  methods: {
    fmt(valor) {
      return (valor || 0).toFixed(2)
    },
    alActivarCaja() {
      if (this.cajaRegistrarPago) this.bancoRegistrarPago = false
    },
    alActivarBanco() {
      if (this.bancoRegistrarPago) {
        this.cajaRegistrarPago = false
        if (this.bancos.length > 0 && !this.bancoSeleccionadoId) {
          this.bancoSeleccionadoId = this.bancos[0].id
        }
      }
    },

    limpiarIdentificacion(valor) {
      return String(valor || '').replace(/\D+/g, '')
    },

    redondear4(valor) {
      return Math.round((Number(valor) || 0) * 10000) / 10000
    },

    parseNumeroXml(valor, fallback = 0) {
      if (valor === null || valor === undefined) return fallback
      const normalizado = String(valor).trim().replace(/\s+/g, '').replace(',', '.')
      const numero = Number(normalizado)
      return Number.isFinite(numero) ? numero : fallback
    },

    obtenerRucEmpresaLogeada() {
      try {
        // Primera fuente: store de Vuex (más confiable, refleja el estado actual)
        const storeUser = this.$store?.getters?.currentUser
        const rucStore = storeUser?.company?.ruc || storeUser?.ruc || ''
        if (rucStore) return this.limpiarIdentificacion(rucStore)

        // Fallback: localStorage
        const raw = localStorage.getItem('usuario_cliente') || localStorage.getItem('client_user')
        if (!raw) return ''
        const usuario = JSON.parse(raw)
        const ruc = usuario?.company?.ruc || usuario?.ruc || ''
        return this.limpiarIdentificacion(ruc)
      } catch (_e) {
        return ''
      }
    },

    obtenerDescuentoAdicionalXml() {
      return this.redondear4(
        this.formulario.detalles.reduce((acc, d) => acc + (Number(d.descuentoAdicional || 0) || 0), 0)
      )
    },

    alertaBootstrap(tipo) {
      const mapa = { exito: 'success', error: 'danger', advertencia: 'warning', informacion: 'info' }
      return mapa[tipo] || 'danger'
    },

    iconoAlerta(tipo) {
      const mapa = {
        exito: 'bi bi-check-circle-fill text-success',
        error: 'bi bi-x-circle-fill text-danger',
        advertencia: 'bi bi-exclamation-triangle-fill text-warning',
        informacion: 'bi bi-info-circle-fill text-info',
      }
      return mapa[tipo] || 'bi bi-exclamation-circle-fill'
    },

    /* ── Catálogos ── */
    async cargarCatalogos() {
      try {
        const [resCat, resUm, resSesiones, resBancos] = await Promise.allSettled([
          api.get('/categorias'),
          api.get('/unidades-medida'),
          api.get('/cajas/sesiones-abiertas'),
          api.get('/bancos'),
        ])
        if (resCat.status === 'fulfilled') {
          this.categorias = resCat.value.data?.data || resCat.value.data || []
        }
        if (resUm.status === 'fulfilled') {
          this.unidadesMedida = resUm.value.data?.data || resUm.value.data || []
        }
        if (resSesiones.status === 'fulfilled') {
          this.sesionesAbiertas = resSesiones.value.data?.sesiones || []
          // sesionActiva = sesión del propio usuario si existe, si no la primera disponible
          const miSesion = this.sesionesAbiertas.find(s => s.usuario_id === this.$store.state.user?.id)
          this.sesionActiva = miSesion || this.sesionesAbiertas[0] || null
          if (this.sesionActiva) this.sesionSeleccionadaId = this.sesionActiva.id
        }
        if (resBancos.status === 'fulfilled') {
          const raw = resBancos.value.data
          this.bancos = raw?.bancos || (Array.isArray(raw) ? raw : [])
        }
      } catch (e) {
        // catálogos no críticos — el usuario puede ingresar sin ellos
      }
    },

    /* ── Proveedor ── */
    async buscarProveedor() {
      const ruc = this.formulario.proveedorRuc.trim()
      if (ruc.length < 10) return
      this.buscandoProveedor = true
      try {
        const { data } = await api.get(`/sri/ruc-catastro/${ruc}`)
        if (data.razon_social || data.nombre_comercial) {
          this.formulario.proveedorNombre = data.razon_social || data.nombre_comercial
        }
      } catch (e) {
        // No encontrado en catastro, el usuario completa manualmente
      } finally {
        this.buscandoProveedor = false
      }
    },

    /* ── Detalles ── */
    agregarDetalle() {
      this.formulario.detalles.push(nuevoDetalle())
    },

    calcularLinea(item) {
      const bruto = (item.precioUnitario || 0) * (item.cantidad || 0)
      item.baseImponible = Math.max(0, bruto - (item.descuento || 0))
      item.montoIva = item.baseImponible * ((item.porcentajeIva || 0) / 100)
      item.totalLinea = item.baseImponible + item.montoIva
    },

    /* ── XML ── */
    onArchivoXmlSeleccionado(evento) {
      const archivo = evento.target.files[0]
      if (!archivo) return
      const lector = new FileReader()
      lector.onload = async (e) => {
        // Limpiar estado previo de duplicado
        this.compraDuplicadaId = null
        this.alerta = { mensaje: '', tipo: 'error' }

        try {
          this.parsearXML(e.target.result)
        } catch (err) {
          window.$notification?.error('Error al procesar el XML: ' + err.message)
          if (this.$refs.archivoXml) this.$refs.archivoXml.value = ''
          return
        }

        // Verificar duplicado en la base de datos al cargar el XML
        if (this.formulario.numeroComprobante) {
          try {
            const { data } = await api.get('/compras/verificar-duplicado', {
              params: { numero_comprobante: this.formulario.numeroComprobante },
            })
            if (data.duplicado) {
              this.compraDuplicadaId = data.compra_id || null
              window.$notification?.warning(`La factura ${this.formulario.numeroComprobante} ya fue registrada anteriormente.`, { duration: 8000 })
            }
          } catch (_e) {
            // Si falla la verificación no se bloquea — se validará igualmente al intentar guardar
          }
        }

        if (this.$refs.archivoXml) this.$refs.archivoXml.value = ''
      }
      lector.readAsText(archivo, 'UTF-8')
    },

    parsearXML(contenido) {
      const parser = new DOMParser()
      const docRaiz = parser.parseFromString(contenido, 'text/xml')

      let numeroAutorizacion = ''
      let estadoSri = ''
      let ambiente = ''
      let docFactura = docRaiz

      const esAutorizacion = !!docRaiz.querySelector('autorizacion')
      if (esAutorizacion) {
        numeroAutorizacion = docRaiz.querySelector('numeroAutorizacion')?.textContent.trim() || ''
        estadoSri         = docRaiz.querySelector('estado')?.textContent.trim() || ''
        ambiente          = docRaiz.querySelector('ambiente')?.textContent.trim() || ''
        const cdataComprobante = docRaiz.querySelector('comprobante')?.textContent.trim() || ''
        if (cdataComprobante) {
          docFactura = parser.parseFromString(cdataComprobante, 'text/xml')
        }
      }

      const get = (doc, sel) => doc.querySelector(sel)?.textContent.trim() || ''

      const identificacionComprador = get(docFactura, 'infoFactura identificacionComprador')
      const ruc             = get(docFactura, 'infoTributaria ruc')
      const razonSocial     = get(docFactura, 'infoTributaria razonSocial')
      const nombreComercial = get(docFactura, 'infoTributaria nombreComercial')
      const estab           = get(docFactura, 'infoTributaria estab')
      const ptoEmi          = get(docFactura, 'infoTributaria ptoEmi')
      const secuencial      = get(docFactura, 'infoTributaria secuencial')
      const claveAcceso     = get(docFactura, 'infoTributaria claveAcceso')
      const codDoc          = get(docFactura, 'infoTributaria codDoc') || '01'

      if (!numeroAutorizacion && claveAcceso) numeroAutorizacion = claveAcceso

      const fechaEmisionRaw = get(docFactura, 'infoFactura fechaEmision')
      const formaPagoXml    = get(docFactura, 'pagos pago formaPago') || '01'
      const numeroComprobante = (estab && ptoEmi && secuencial)
        ? `${estab}-${ptoEmi}-${secuencial}`
        : ''

      const mapaTipo = { '01': '01', '03': '03', '05': '05' }
      const tipoComprobante = mapaTipo[codDoc] || '01'

      let fechaEmision = fechaEmisionRaw
      if (fechaEmisionRaw && fechaEmisionRaw.includes('/')) {
        const [dia, mes, anio] = fechaEmisionRaw.split('/')
        if (dia && mes && anio) fechaEmision = `${anio}-${mes.padStart(2,'0')}-${dia.padStart(2,'0')}`
      }

      if (!ruc) throw new Error('No se pudo identificar el RUC del emisor en el XML.')

      const rucEmpresaLogeada = this.obtenerRucEmpresaLogeada()
      const idCompradorLimpio = this.limpiarIdentificacion(identificacionComprador)
      if (!idCompradorLimpio) {
        throw new Error('El XML no contiene identificacionComprador.')
      }
      if (!rucEmpresaLogeada) {
        throw new Error('No se pudo verificar el RUC de la empresa logeada. Recargue la página o vuelva a iniciar sesión.')
      }
      if (idCompradorLimpio !== rucEmpresaLogeada) {
        throw new Error(`La identificación del comprador en el XML (${idCompradorLimpio}) no coincide con el RUC de la empresa logeada (${rucEmpresaLogeada}). Este comprobante pertenece a otra empresa.`)
      }

      const descuentosAdicionales = Array.from(
        docFactura.querySelectorAll('totalConImpuestos totalImpuesto descuentoAdicional')
      ).map(n => this.parseNumeroXml(n?.textContent, 0))
      const descuentoAdicionalTotal = this.redondear4(
        descuentosAdicionales.reduce((acc, val) => acc + val, 0)
      )

      // Descuento de líneas = suma de <descuento> de cada <detalle>
      // (NO usar totalDescuento de infoFactura porque ese ya incluye el descuentoAdicional)
      const descuentoLineasXml = this.redondear4(
        Array.from(docFactura.querySelectorAll('detalle descuento'))
          .reduce((acc, n) => acc + this.parseNumeroXml(n?.textContent, 0), 0)
      )

      const detallesXml = Array.from(docFactura.querySelectorAll('detalle'))
      const detalles = detallesXml.map((d, index) => {
        const precioUnitarioBruto = this.parseNumeroXml(d.querySelector('precioUnitario')?.textContent, 0)
        const cantidad            = this.parseNumeroXml(d.querySelector('cantidad')?.textContent, 1)
        const descuento           = this.parseNumeroXml(d.querySelector('descuento')?.textContent, 0)
        const tarifa              = d.querySelector('impuesto tarifa')
        const porcentajeIva       = tarifa ? this.parseNumeroXml(tarifa.textContent, 15) : 15

        // Calcular precio unitario neto: si hay descuento se prorratea por unidad
        const precioUnitario = descuento > 0 && cantidad > 0
          ? this.redondear4(precioUnitarioBruto - descuento / cantidad)
          : precioUnitarioBruto

        // Base imponible y totales con el precio ya neto
        const baseImponible = this.redondear4(precioUnitario * cantidad)
        const montoIva      = this.redondear4(baseImponible * (porcentajeIva / 100))
        const totalLinea    = this.redondear4(baseImponible + montoIva)

        return {
          codigo: decodificarTexto(d.querySelector('codigoPrincipal')?.textContent.trim() || ''),
          descripcion: decodificarTexto(d.querySelector('descripcion')?.textContent.trim() || ''),
          precioUnitario,
          cantidad,
          descuento: 0, // ya incorporado en precioUnitario
          descuentoAdicional: index === 0 ? descuentoAdicionalTotal : 0,
          porcentajeIva,
          baseImponible,
          montoIva,
          totalLinea,
        }
      })

      // ── Totales directos del XML ──────────────────────────────────────────
      const totalSinImpuestosXml = this.parseNumeroXml(get(docFactura, 'infoFactura totalSinImpuestos'), 0)
      const importeTotalXml      = this.parseNumeroXml(get(docFactura, 'infoFactura importeTotal'), 0)
      // IVA: en SRI Ecuador el codigo del IVA es 2 (no 3)
      // En totalConImpuestos el porcentaje puede venir en <tarifa> o en <codigoPorcentaje>:
      //   codigoPorcentaje: 0=0%, 2=5%, 3=12%, 4=15%
      const mapaCodigoPorcentaje = { '0': 0, '2': 5, '3': 12, '4': 15 }
      const impuestosXml = {}
      docFactura.querySelectorAll('totalConImpuestos totalImpuesto').forEach(imp => {
        const codImp = imp.querySelector('codigo')?.textContent.trim()
        if (codImp === '2') {  // 2 = IVA (SRI Ecuador)
          const valor   = this.parseNumeroXml(imp.querySelector('valor')?.textContent, 0)
          // Preferir <tarifa> si existe; si no, mapear desde <codigoPorcentaje>
          const tarifaEl = imp.querySelector('tarifa')
          let tarifa
          if (tarifaEl) {
            tarifa = this.parseNumeroXml(tarifaEl.textContent, 0)
          } else {
            const codPct = imp.querySelector('codigoPorcentaje')?.textContent.trim() || '4'
            tarifa = mapaCodigoPorcentaje[codPct] ?? 15
          }
          impuestosXml[tarifa] = (impuestosXml[tarifa] || 0) + valor
        }
      })

      this.formulario.totalesXml = {
        descuentoLineas:    descuentoLineasXml,       // suma de <descuento> de cada <detalle>
        descuentoAdicional: descuentoAdicionalTotal,  // <descuentoAdicional> en totalConImpuestos
        totalSinImpuestos:  totalSinImpuestosXml,     // <totalSinImpuestos> de infoFactura
        impuestos:          impuestosXml,             // { 15: 27.21 } o { 15: x, 5: y }
        importeTotal:       importeTotalXml,          // <importeTotal> = total a pagar
      }
      // ─────────────────────────────────────────────────────────────────────

      this.formulario.numeroAutorizacion      = numeroAutorizacion
      this.formulario.estadoSri               = estadoSri
      this.formulario.ambiente                = ambiente
      this.formulario.tipoComprobante         = tipoComprobante
      this.formulario.numeroComprobante       = numeroComprobante
      this.formulario.fechaEmision            = fechaEmision
      this.formulario.identificacionComprador = idCompradorLimpio
      this.formulario.proveedorRuc            = ruc
      this.formulario.proveedorNombre         = decodificarTexto(razonSocial)
      this.formulario.proveedorNombreComercial = decodificarTexto(nombreComercial)
      this.formulario.formaPago               = formaPagoXml
      this.formulario.detalles                = detalles.length > 0 ? detalles : [nuevoDetalle()]
      this.formulario.detalles.forEach(item => this.calcularLinea(item))
      this.xmlCargado = true
    },

    limpiarXml() {
      this.xmlCargado = false
      this.formulario.estadoSri   = ''
      this.formulario.ambiente    = ''
      this.formulario.totalesXml  = null
    },

    /* ── Validación ── */
    validar() {
      const f = this.formulario
      if (!f.numeroAutorizacion)
        return 'El número de autorización es obligatorio.'
      if (f.numeroAutorizacion.length !== 49)
        return `El número de autorización debe tener 49 dígitos (actual: ${f.numeroAutorizacion.length}).`
      if (!/^\d+$/.test(f.numeroAutorizacion))
        return 'El número de autorización solo debe contener dígitos.'
      if (!f.proveedorRuc.trim())
        return 'Ingrese el RUC o identificación del proveedor.'
      if (!f.proveedorNombre.trim())
        return 'Ingrese la razón social del proveedor.'
      if (!f.numeroComprobante.trim())
        return 'Ingrese el número del comprobante (Ej: 001-001-000000001).'
      if (!f.fechaEmision)
        return 'Ingrese la fecha de emisión del comprobante.'
      if (f.identificacionComprador) {
        const rucEmpresa = this.obtenerRucEmpresaLogeada()
        const idComprador = this.limpiarIdentificacion(f.identificacionComprador)
        if (!rucEmpresa) {
          return 'No se pudo verificar el RUC de la empresa. Recargue la página o vuelva a iniciar sesión.'
        }
        if (idComprador !== rucEmpresa) {
          return `La identificación del comprador del XML (${idComprador}) debe coincidir con el RUC de la empresa logeada (${rucEmpresa}).`
        }
      }
      if (f.detalles.length === 0)
        return 'Agregue al menos un ítem al detalle de la compra.'
      for (const d of f.detalles) {
        if (!d.descripcion.trim())
          return 'Complete la descripción de todos los ítems del detalle.'
      }
      return null
    },

    /* ── Revisar (paso 1 → paso 2) ── */
    async revisarProductos() {
      this.alerta = { mensaje: '', tipo: 'error' }

      // Si la factura ya existe en el sistema, no tiene sentido continuar
      if (this.compraDuplicadaId) {
        window.$notification?.warning('Esta factura ya fue registrada. No puedes continuar con un comprobante duplicado.')
        window.scrollTo({ top: 0, behavior: 'smooth' })
        return
      }

      const errorValidacion = this.validar()
      if (errorValidacion) {
        this.alerta = { mensaje: errorValidacion, tipo: 'advertencia' }
        window.scrollTo({ top: 0, behavior: 'smooth' })
        return
      }

      // Construir array de homologación desde los detalles del formulario
      this.homologacion = this.formulario.detalles.map(d => ({
        ...d,
        accion: 'vincular',
        candidatos: [],
        cargandoCandidatos: true,
        productoVinculado: null,
        actualizarCosto: false,
        opcionCostoCero: 'sin_stock', // 'sin_stock' | 'ultimo_costo' (solo activo cuado precioUnitario === 0)
        preciosVinculado: [],
        cargandoPreciosVinculado: false,
        convertirUnidad: false,
        factorConversion: null,
        unidadConversionId: null,
        busquedaManual: '',
        resultadosBusqueda: [],
        buscandoManual: false,
        nuevoCodigo: d.codigo || '',
        nuevoCodigoAuxiliar: '',
        categoriaId: '',
        unidadMedidaId: '',
        tipoProd: 'bien',
        precios: [
          { tipo_precio: 'Precio1', porcentaje: 30, precio: 0, activo: true },
          { tipo_precio: 'Precio2', porcentaje: 20, precio: 0, activo: true },
          { tipo_precio: 'Precio3', porcentaje: 10, precio: 0, activo: false },
        ],
      }))

      // Calcular precios iniciales
      this.homologacion.forEach(hItem => {
        hItem.precios.forEach(p => this.calcularPrecio(hItem, p))
      })

      this.paso = 2
      window.scrollTo({ top: 0, behavior: 'smooth' })

      // Cargar candidatos para cada ítem en paralelo
      this.homologacion.forEach(hItem => this.cargarCandidatos(hItem))
    },

    volverFormulario() {
      this.paso = 1
      this.homologacion = []
      this.alerta = { mensaje: '', tipo: 'error' }
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    /* ── Homologación: candidatos ── */
    async cargarCandidatos(hItem) {
      hItem.cargandoCandidatos = true
      try {
        const { data } = await api.get('/productos/buscar-homologacion', {
          params: {
            codigo: hItem.codigo || '',
            nombre: hItem.descripcion || '',
          },
        })
        hItem.candidatos = data.data || []

        // Auto-seleccionar si hay coincidencia exacta de código
        if (hItem.candidatos.length > 0 && hItem.candidatos[0].codigo_principal === hItem.codigo) {
          this.seleccionarProducto(hItem, hItem.candidatos[0])
        }

        // Si no hay candidatos, cambiar a modo "crear"
        if (hItem.candidatos.length === 0) {
          hItem.accion = 'crear'
        }
      } catch (e) {
        hItem.candidatos = []
        hItem.accion = 'crear'
      } finally {
        hItem.cargandoCandidatos = false
      }
    },

    async buscarProductoManual(hItem) {
      const q = hItem.busquedaManual.trim()
      if (!q) return
      hItem.buscandoManual = true
      try {
        const { data } = await api.get('/productos/buscar-homologacion', {
          params: { codigo: q, nombre: q },
        })
        hItem.resultadosBusqueda = data.data || []
      } catch (e) {
        hItem.resultadosBusqueda = []
      } finally {
        hItem.buscandoManual = false
      }
    },

    debounceBuscarManual(hItem) {
      if (hItem._debounceTimer) clearTimeout(hItem._debounceTimer)

      // Al escribir una nueva búsqueda, limpiar el producto vinculado automáticamente
      if (hItem.productoVinculado) {
        hItem.productoVinculado = null
        hItem.actualizarCosto = false
        hItem.opcionCostoCero = 'sin_stock'
        hItem.preciosVinculado = []
        hItem.convertirUnidad = false
        hItem.factorConversion = null
        hItem.unidadConversionId = null
      }

      if (!hItem.busquedaManual || hItem.busquedaManual.trim().length < 2) {
        hItem.resultadosBusqueda = []
        return
      }
      hItem._debounceTimer = setTimeout(() => {
        this.buscarProductoManual(hItem)
      }, 350)
    },

    seleccionarProducto(hItem, producto) {
      hItem.productoVinculado = producto
      hItem.actualizarCosto = parseFloat(producto.costo) !== hItem.precioUnitario
      hItem.resultadosBusqueda = []

      // Auto-detectar conversión: si el producto está registrado en una unidad de medida
      // (longitud, peso, volumen, área) se activa automáticamente el panel de conversión
      // para que el usuario solo deba ingresar el factor (ej: metros por rollo).
      const TIPOS_CONVERSION = ['longitud', 'peso', 'volumen', 'area']
      if (producto.unidad_medida_id) {
        const unidad = this.unidadesMedida.find(u => u.id == producto.unidad_medida_id)
        if (unidad && TIPOS_CONVERSION.includes(unidad.tipo)) {
          hItem.convertirUnidad    = true
          hItem.unidadConversionId = producto.unidad_medida_id

          // Pre-cargar el factor guardado en el producto (si existe)
          const factorGuardado = parseFloat(producto.factor_conversion)
          if (factorGuardado > 1) {
            hItem.factorConversion = factorGuardado
          }
        }
      }

      this.cargarPreciosVinculado(hItem)
    },

    async cargarPreciosVinculado(hItem) {
      if (!hItem.productoVinculado?.id) return
      hItem.cargandoPreciosVinculado = true
      try {
        const { data } = await api.get(`/productos/${hItem.productoVinculado.id}/precios`)
        const preciosCargados = Array.isArray(data.data) ? data.data : []
        const costoBase = hItem.precioUnitario || parseFloat(hItem.productoVinculado.costo) || 1

        let nuevosPrecios
        if (preciosCargados.length > 0) {
          nuevosPrecios = preciosCargados.map(p => {
            const precioGuardado = parseFloat(p.precio) || 0
            const porcentaje = costoBase > 0
              ? Math.max(0, Math.round((precioGuardado / costoBase - 1) * 1000) / 10)
              : 0
            return {
              tipo_precio: p.tipo_precio,
              porcentaje,
              precio: precioGuardado,
              activo: Number(p.activo) !== 0,
            }
          })
        } else {
          nuevosPrecios = [
            { tipo_precio: 'Precio1', porcentaje: 30, precio: 0, activo: true },
            { tipo_precio: 'Precio2', porcentaje: 20, precio: 0, activo: true },
            { tipo_precio: 'Precio3', porcentaje: 10, precio: 0, activo: false },
          ]
        }
        // Recalcular usando el costo de la factura como nueva base
        nuevosPrecios.forEach(p => this.calcularPrecioVinculado(hItem, p))
        hItem.preciosVinculado = nuevosPrecios
        // Pre-seleccionar la unidad de medida actual del producto como destino de conversión
        if (!hItem.unidadConversionId && hItem.productoVinculado?.unidad_medida_id) {
          hItem.unidadConversionId = hItem.productoVinculado.unidad_medida_id
        }
      } catch (_e) {
        const preciosFallback = [
          { tipo_precio: 'Precio1', porcentaje: 30, precio: 0, activo: true },
          { tipo_precio: 'Precio2', porcentaje: 20, precio: 0, activo: true },
          { tipo_precio: 'Precio3', porcentaje: 10, precio: 0, activo: false },
        ]
        preciosFallback.forEach(p => this.calcularPrecioVinculado(hItem, p))
        hItem.preciosVinculado = preciosFallback
      } finally {
        hItem.cargandoPreciosVinculado = false
      }
    },

    calcularPrecioVinculado(hItem, p) {
      // Base: si el usuario marcó "actualizar costo" se usa el precio de factura (nuevo costo),
      // de lo contrario se usa el costo registrado actualmente en el producto.
      const costoFactura  = hItem.precioUnitario || 0
      const costoRegistrado = parseFloat(hItem.productoVinculado?.costo) || costoFactura
      const costoBase = hItem.actualizarCosto ? costoFactura : costoRegistrado

      // Si hay conversión de unidades, dividir por el factor para obtener costo/unidad mínima
      const factor = (hItem.convertirUnidad && hItem.factorConversion > 1)
        ? hItem.factorConversion
        : 1
      const base = factor > 1 ? costoBase / factor : costoBase
      p.precio = Math.round(base * (1 + p.porcentaje / 100) * 10000) / 10000
    },

    agregarPrecioVinculado(hItem) {
      const tipos = ['Precio1', 'Precio2', 'Precio3', 'Precio4', 'Precio5']
      const usados = new Set(hItem.preciosVinculado.map(p => p.tipo_precio))
      const siguiente = tipos.find(t => !usados.has(t))
      if (!siguiente) return
      const nuevoPrecio = { tipo_precio: siguiente, porcentaje: 0, precio: 0, activo: true }
      this.calcularPrecioVinculado(hItem, nuevoPrecio)
      hItem.preciosVinculado.push(nuevoPrecio)
    },

    agregarPrecioCrear(hItem) {
      const tipos = ['Precio1', 'Precio2', 'Precio3', 'Precio4', 'Precio5']
      const usados = new Set(hItem.precios.map(p => p.tipo_precio))
      const siguiente = tipos.find(t => !usados.has(t))
      if (!siguiente) return
      const nuevoPrecio = { tipo_precio: siguiente, porcentaje: 0, precio: 0, activo: true }
      this.calcularPrecio(hItem, nuevoPrecio)
      hItem.precios.push(nuevoPrecio)
    },

    calcularPrecio(hItem, p) {
      const factor = (hItem.convertirUnidad && hItem.factorConversion > 1)
        ? hItem.factorConversion
        : 1
      const base = factor > 1 ? hItem.precioUnitario / factor : hItem.precioUnitario
      p.precio = Math.round(base * (1 + p.porcentaje / 100) * 10000) / 10000
    },

    /* ── Guardar (paso 2) ── */
    async guardar() {
      this.alerta = { mensaje: '', tipo: 'error' }

      // Validar: vincular sin producto seleccionado
      const sinVincular = this.homologacion.filter(h => h.accion === 'vincular' && !h.productoVinculado)
      if (sinVincular.length > 0) {
        this.alerta = {
          mensaje: `${sinVincular.length} ítem(s) en modo "Vincular" no tienen un producto seleccionado. Seleccione un producto o cambie la acción a "Crear" u "Omitir".`,
          tipo: 'advertencia',
        }
        window.scrollTo({ top: 0, behavior: 'smooth' })
        return
      }

      // Validar: crear sin categoría
      const sinCategoria = this.homologacion.filter(h => h.accion === 'crear' && !h.categoriaId)
      if (sinCategoria.length > 0) {
        this.alerta = {
          mensaje: `${sinCategoria.length} ítem(s) nuevos no tienen categoría seleccionada.`,
          tipo: 'advertencia',
        }
        return
      }

      this.procesando = true
      try {
        const t = this.totales
        const payload = {
          clave_acceso_sri:            this.formulario.numeroAutorizacion,
          estado_sri:                  this.formulario.estadoSri || null,
          ambiente_sri:                this.formulario.ambiente || null,
          tipo_comprobante:            this.formulario.tipoComprobante,
          numero_comprobante:          this.formulario.numeroComprobante,
          fecha_compra:                this.formulario.fechaEmision,
          identificacion_comprador:    this.formulario.identificacionComprador || null,
          proveedor_ruc:               this.formulario.proveedorRuc,
          proveedor_nombre:            this.formulario.proveedorNombre,
          proveedor_nombre_comercial:  this.formulario.proveedorNombreComercial || null,
          forma_pago:                  this.formulario.formaPago,
          observaciones:               this.formulario.observaciones || null,
          es_credito:                  this.formulario.esCredito ? 1 : 0,
          dias_plazo_credito:          this.formulario.esCredito ? this.formulario.diasPlazo : 0,
          subtotal:                    t.subtotalSinIva,
          descuento_adicional:         t.descuentoAdicional,
          iva_total:                   t.ivaQuince + t.ivaCinco,
          total:                       t.total,
          pago: (() => {
            if (this.formulario.esCredito) return null
            if (this.bancoRegistrarPago && this.bancoSeleccionadoId) {
              return { destino: 'banco', banco_id: this.bancoSeleccionadoId, referencia: this.pagoReferencia || null }
            }
            const sesId = this.sesionSeleccionadaId || this.sesionActiva?.id
            if (this.cajaRegistrarPago && sesId) {
              return { destino: 'caja', sesion_id: sesId, referencia: this.pagoReferencia || null }
            }
            return null
          })(),
          detalles: this.homologacion.map(h => ({
            codigo:           h.codigo,
            descripcion:      h.descripcion,
            precio_unitario:  h.precioUnitario,
            cantidad:         h.cantidad,
            descuento:        h.descuento || 0,
            porcentaje_iva:   h.porcentajeIva,
            // Homologación
            accion:           h.accion,
            producto_id:      h.accion === 'vincular' ? (h.productoVinculado?.id || null) : null,
            actualizar_costo: h.accion === 'vincular' ? !!h.actualizarCosto : false,
            // Opciones costo cero (solo aplica cuando precio_unitario === 0 y accion === vincular)
            sin_stock:        h.accion === 'vincular' && h.precioUnitario === 0 && h.opcionCostoCero === 'sin_stock',
            usar_ultimo_costo: h.accion === 'vincular' && h.precioUnitario === 0 && h.opcionCostoCero === 'ultimo_costo',
            // Factor de conversión de unidades (ej: 1 rollo = 305 metros) — solo afecta cantidades si > 1
            factor_conversion: h.accion !== 'omitir' && h.convertirUnidad && h.factorConversion > 1
              ? h.factorConversion
              : null,
            // Unidad destino — actualiza unidad_medida_id del producto siempre que la conversión esté activa
            unidad_conversion_id: h.accion !== 'omitir' && h.convertirUnidad && h.unidadConversionId
              ? h.unidadConversionId
              : null,
            // Precios de venta editados al vincular
            precios_vinculado: h.accion === 'vincular' && h.preciosVinculado.length > 0
              ? h.preciosVinculado.map(p => ({ tipo_precio: p.tipo_precio, precio: p.precio, activo: p.activo ? 1 : 0 }))
              : null,
            // Crear nuevo
            nuevo_codigo:           h.accion === 'crear' ? (h.nuevoCodigo || h.codigo || null) : null,
            nuevo_codigo_auxiliar:  h.accion === 'crear' ? (h.nuevoCodigoAuxiliar || null) : null,
            categoria_id:           h.accion === 'crear' ? (h.categoriaId || null) : null,
            unidad_medida_id: h.accion === 'crear' ? (h.unidadMedidaId || null) : null,
            tipo_producto:    h.accion === 'crear' ? (h.tipoProd || 'bien') : null,
            precios:          h.accion === 'crear'
              ? h.precios.filter(p => p.activo).map(p => ({ tipo_precio: p.tipo_precio, precio: p.precio, activo: 1 }))
              : null,
          })),
        }

        const { data } = await api.post('/compras', payload)
        if (data.success) {
          this.compraDuplicadaId = null
          let msg = 'Compra registrada correctamente.'
          if (data.productos_creados > 0) {
            msg += ` Se crearon ${data.productos_creados} producto(s) nuevos en el inventario.`
          }
          window.$notification?.success(msg, { duration: 3000 })
          setTimeout(() => this.$router.push('/compras/ordenes'), 2000)
        } else {
          window.$notification?.error(data.error || 'Error al registrar la compra.')
        }
      } catch (e) {
        const respData = e.response?.data
        if (e.response?.status === 409 && respData?.duplicado) {
          this.compraDuplicadaId = respData.compra_id || null
          window.$notification?.warning('Esta factura ya fue registrada anteriormente.')
        } else {
          this.compraDuplicadaId = null
          window.$notification?.error(respData?.error || 'No se pudo conectar con el servidor.')
        }
      } finally {
        this.procesando = false
      }
    },
  },
}
</script>

<style scoped>
/* ── Contenedor principal ── */
.compra-registrar {
  padding: 1.5rem;
}

.compra-registrar__titulo {
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--text-primary);
}

.compra-registrar__subtitulo {
  font-size: 0.875rem;
  color: var(--text-secondary);
}

/* ── Secciones ── */
.compra-seccion {
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}

.compra-seccion__cabecera {
  background: var(--primary-color);
  color: #fff;
  font-weight: 600;
  font-size: 0.9rem;
  padding: 0.65rem 1.1rem;
  letter-spacing: 0.01em;
}

.compra-seccion__cuerpo {
  padding: 1.25rem 1.25rem;
}

/* ── Campo de autorización prominente ── */
.compra-input-autorizacion {
  font-size: 0.92rem;
  letter-spacing: 0.08em;
  background: var(--input-bg);
  color: var(--text-primary);
  border-color: var(--border-main);
}

.compra-input-autorizacion:focus {
  border-color: var(--border-focus);
  box-shadow: 0 0 0 3px rgba(17, 115, 212, 0.15);
}

.compra-badge-estado {
  font-size: 0.8rem !important;
  white-space: nowrap;
}

/* ── Tabla de detalles ── */
.compra-tabla-wrapper {
  overflow-x: auto;
  border-top: 1px solid var(--border-main);
}

.compra-tabla {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  min-width: 960px;
}

.compra-tabla thead tr {
  background: var(--secondary-bg-soft, #f1f5f9);
}

.dark-theme .compra-tabla thead tr {
  background: var(--bg-tertiary);
}

.compra-tabla th {
  padding: 0.65rem 0.75rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 2px solid var(--border-main);
  border-right: 1px solid var(--border-main);
  white-space: nowrap;
}

.compra-tabla th:last-child { border-right: none; }

/* Anchos de columnas */
.th-codigo      { width: 110px; }
.th-descripcion { min-width: 220px; }
.th-num         { width: 110px; text-align: right; }
.th-sm          { width: 80px; text-align: center; }
.th-total       { background: var(--accent-info-light, rgba(17,115,212,0.07)); }
.th-accion      { width: 44px; }

/* Celdas editables */
.td-editable {
  padding: 0;
  border-right: 1px solid var(--border-main);
  border-bottom: 1px solid var(--border-main);
}

.td-editable:last-of-type { border-right: none; }

.fila-detalle:hover .td-editable {
  background: var(--table-hover);
}

.input-celda {
  width: 100%;
  height: 38px;
  padding: 0.35rem 0.55rem;
  border: none;
  outline: none;
  background: transparent;
  color: var(--text-primary);
  font-size: 0.875rem;
  box-sizing: border-box;
}

.input-celda:focus {
  background: var(--accent-info-light, rgba(17,115,212,0.07));
  outline: 2px solid var(--border-focus);
  outline-offset: -2px;
}

select.input-celda {
  cursor: pointer;
  padding-right: 0.25rem;
}

/* Celdas de sólo lectura (calculadas) */
.td-calculado {
  padding: 0 0.75rem;
  border-right: 1px solid var(--border-main);
  border-bottom: 1px solid var(--border-main);
  font-size: 0.875rem;
  color: var(--text-secondary);
  white-space: nowrap;
  vertical-align: middle;
}

.td-total {
  font-weight: 600;
  color: var(--text-primary) !important;
  background: var(--accent-info-light, rgba(17,115,212,0.06));
}

.td-accion {
  padding: 0.25rem;
  border-bottom: 1px solid var(--border-main);
  vertical-align: middle;
}

/* Tema oscuro - ajustes de inputs dentro de tabla */
.dark-theme .input-celda {
  color: var(--text-primary);
}

.dark-theme .input-celda:focus {
  background: var(--accent-info-light);
}

/* ── Totales ── */
.compra-fila-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.4rem 0;
  font-size: 0.9rem;
  color: var(--text-secondary);
  border-bottom: 1px solid var(--border-main);
}

.compra-fila-total:last-child { border-bottom: none; }

.compra-fila-total--grande {
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--text-primary);
  padding-top: 0.6rem;
}

/* --- HOMOLOGACIÓN DE PRODUCTOS --- */

/* Ítem de homologación */
.homolog-item {
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}

.homolog-item__header {
  background: var(--secondary-bg-soft, #f8fafc);
  border-bottom: 1px solid var(--border-main);
  padding: 0.45rem 0.9rem;
}

.dark-theme .homolog-item__header {
  background: var(--bg-tertiary);
}

.homolog-item__body {
  padding: 0.65rem 0.9rem;
}

/* Opciones de acción */
.homolog-opciones {
  flex-wrap: wrap;
}

.homolog-opcion {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 0.45rem 0.9rem;
  border: 1px solid var(--border-main);
  border-radius: 8px;
  font-size: 0.875rem;
  background: var(--secondary-bg-main);
  color: var(--text-primary);
  transition: background 0.15s, border-color 0.15s;
  user-select: none;
}

.homolog-opcion:hover {
  background: var(--accent-info-light, rgba(17,115,212,0.07));
  border-color: var(--primary-color);
}

.homolog-opcion--activa {
  border-color: var(--primary-color);
  background: var(--accent-info-light, rgba(17,115,212,0.1));
  font-weight: 600;
}

/* Variante compacta de opción */
.homolog-opcion--sm {
  padding: 0.28rem 0.55rem;
  font-size: 0.8rem;
  border-radius: 6px;
}

/* Panel de contenido por opción */
.homolog-panel {
  background: var(--secondary-bg-soft, #f8fafc);
  border: 1px solid var(--border-main);
  border-radius: 8px;
  padding: 0.6rem 0.8rem;
}

.dark-theme .homolog-panel {
  background: var(--bg-tertiary);
}

/* Lista de candidatos */
.homolog-candidatos {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  max-height: 240px;
  overflow-y: auto;
}

.homolog-candidato {
  padding: 0.6rem 0.9rem;
  border: 1px solid var(--border-main);
  border-radius: 6px;
  cursor: pointer;
  background: var(--secondary-bg-main);
  transition: background 0.12s, border-color 0.12s;
  font-size: 0.875rem;
}

.homolog-candidato:hover {
  background: var(--accent-info-light, rgba(17,115,212,0.08));
  border-color: var(--primary-color);
}

.homolog-candidato--seleccionado {
  border-color: var(--primary-color);
  background: var(--accent-info-light, rgba(17,115,212,0.12));
}

/* Fila compacta de candidato */
.homolog-candidato--compact {
  display: flex;
  align-items: center;
  padding: 0.3rem 0.65rem;
  border-radius: 6px;
}
.homolog-candidato--compact:last-child { border-bottom: none; }
.homolog-candidato--compact:hover { background: var(--table-hover); }

/* Producto seleccionado confirmado */
.homolog-seleccionado {
  padding: 0.85rem 1rem;
  border: 1px solid var(--accent-success, #28a745);
  border-radius: 8px;
  background: rgba(40, 167, 69, 0.06);
  font-size: 0.875rem;
}

/* Crédito */
.compra-plazos-rapidos {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.compra-plazo-btn {
  font-size: .75rem;
  font-weight: 600;
  padding: 1px 8px;
  border: 1px solid var(--border-main, #ced4da);
  border-radius: 4px;
  background: var(--secondary-bg-soft, #f8f9fa);
  color: var(--primary-color, #0d6efd);
  cursor: pointer;
  line-height: 1.6;
  transition: background .12s, border-color .12s;
}
.compra-plazo-btn:hover {
  background: var(--primary-color, #0d6efd);
  border-color: var(--primary-color, #0d6efd);
  color: #fff;
}

.compra-vencimiento-info {
  font-size: .875rem;
  color: var(--text-secondary, #495057);
  background: var(--secondary-bg-soft, #f8f9fa);
  border: 1px solid var(--border-main, #ced4da);
  border-radius: 8px;
  padding: .45rem .85rem;
  margin-bottom: 2px;
}

/* --- ALERTAS DEL SISTEMA --- */
.alerta-sistema {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border-radius: 8px;
  border: 1px solid transparent;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  box-shadow: var(--shadow-sm);
}

.alerta-sistema--exito {
  background: rgba(16, 185, 129, 0.1);
  border-color: var(--accent-success);
  color: var(--accent-success);
}

.alerta-sistema--error {
  background: rgba(239, 68, 68, 0.1);
  border-color: var(--accent-danger);
  color: var(--accent-danger);
}

.alerta-sistema--advertencia {
  background: rgba(245, 158, 11, 0.1);
  border-color: var(--accent-warning);
  color: var(--accent-warning);
}

.alerta-sistema--informacion {
  background: rgba(14, 165, 233, 0.1);
  border-color: var(--primary-main);
  color: var(--primary-main);
}

.alerta-sistema__icono {
  font-size: 1.1rem;
  flex-shrink: 0;
}

.alerta-sistema__texto {
  flex: 1;
  color: var(--text-primary);
  line-height: 1.4;
}

.alerta-sistema__cerrar {
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.2rem;
  color: var(--text-secondary);
  border-radius: 4px;
  display: flex;
  align-items: center;
  transition: color 0.15s, background 0.15s;
}

.alerta-sistema__cerrar:hover {
  color: var(--text-primary);
  background: rgba(0,0,0,0.06);
}

/* Panel duplicado con botones de acción */
.alerta-sistema--con-acciones {
  align-items: flex-start;
}

.alerta-sistema__cuerpo {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.alerta-sistema__botones {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.alerta-sistema__btn {
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.8rem;
  border-radius: 6px;
  border: 1px solid transparent;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.alerta-sistema__btn--principal {
  background: var(--accent-warning);
  border-color: var(--accent-warning);
  color: #fff;
}

.alerta-sistema__btn--principal:hover {
  filter: brightness(0.9);
}

.alerta-sistema__btn--secundario {
  background: transparent;
  border-color: var(--border-main);
  color: var(--text-secondary);
}

.alerta-sistema__btn--secundario:hover {
  background: var(--border-main);
  color: var(--text-primary);
}
</style>



